import React, { useContext, useEffect, useState } from 'react'
import {
    Table,
    Button,
    Dropdown,
    Typography,
    Affix,
    Form,
    Modal,
    App,
    Space,
    Input
} from 'antd'
import { isMobileOnly } from "react-device-detect"
import _ from "lodash";
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaEllipsisH, FaSearch } from "react-icons/fa"
import { API, Enum, AntDesign } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

// Components
import CURecord from './cu-record'

const { Pages: { UserManagement: { Permission: PermissionLanguage } }, Share, Message: MessageLanguage } = language
const {  USER_MANAGEMENT_PERMISSION_PAGINATE, USER_MANAGEMENT_PERMISSION_REMOVE } = API
const { Message } = Enum
const { theme: { token: { colorPrimary } } } = AntDesign

interface PermissionType {
    id: string,
    name: string,
    caption: string,
}

export default function Index() {

    const [, forceUpdate] = useState({})
    const [form] = Form.useForm()
    const [permissions, setPermissions] = useState<PermissionType[]>([])
    const [record, setRecord] = useState<PermissionType|null>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false)
    const [sort, setSort] = useState<string|false|null|undefined>('createdAt,desc')
    const [name, setName] = useState<string>()
    const [caption, setCaption] = useState<string>()
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    });
    const { axios } = useContext(AxiosContext)
    const { message, modal } = App.useApp();

    const columns: ColumnsType<PermissionType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            title: PermissionLanguage.name,
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Input
                        allowClear
                        placeholder={PermissionLanguage.name}
                        onChange={({ target: { value: name } }) => !name && setName(name)}
                        onBlur={({ target: { value: name } }) => setName(name)}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={name && colorPrimary} />
        },
        {
            title: PermissionLanguage.caption,
            dataIndex: 'caption',
            key: 'caption',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Input
                        allowClear
                        placeholder={PermissionLanguage.caption}
                        onChange={({ target: { value: caption } }) => !caption && setCaption(caption)}
                        onBlur={({ target: { value: caption } }) => setCaption(caption)}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={caption && colorPrimary} />
        },
        {
            title: PermissionLanguage.createdAt,
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('YYYY/MM/DD')
        },
        {
            title: Share.operation,
            dataIndex: "operation",
            key: "operation",
            render: (text, record) =>  <Dropdown menu={{ items: [
                    {
                        key: 'edit',
                        label: <Typography.Link onClick={() => {
                            setRecord(record)
                            setIsModalOpen(true)
                        }}>{ Share.edit }</Typography.Link>,
                    },
                    {
                        key: 'delete',
                        label: <Typography.Link onClick={() => modal.confirm({
                            title: Share.deleteConfirmTitle,
                            okText: Share.deleteConfirmOk,
                            okType: 'danger',
                            cancelText: Share.deleteConfirmCancel,
                            onOk() {
                                setLoadingRecords(true)
                                axios.delete(USER_MANAGEMENT_PERMISSION_REMOVE + record.id).then((response: {}) => {
                                    const { total = 1, pageSize = 1, current = 1 } = pagination
                                    const params = {
                                        page: total % pageSize !== 1 ? current : current - 1,
                                        limit: pageSize,
                                        sort,
                                    };
                                    getPermissions(params)
                                    message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                                }).catch((error: AxiosError) => {
                                    const { response: { data } } = error;
                                    // @ts-ignore
                                    message.error(MessageLanguage[data.message]).then(response => response )
                                    setLoadingRecords(false)
                                })
                            }
                        })}>{ Share.delete }</Typography.Link>,
                    },
                ] }} placement="bottomRight"><a><FaEllipsisH /></a></Dropdown>
        }
    ]

    const getPermissions = (params: AntTableParams) => {
        setLoadingRecords(true)
        const filters: {
            name?: string,
            caption?: string,
        } = {};
        if (name && !_.isEmpty(name)) filters.name = name
        if (caption && !_.isEmpty(caption)) filters.caption = caption
        axios.post(USER_MANAGEMENT_PERMISSION_PAGINATE, filters, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: permissions, page: current, limit: pageSize, totalDocs: total } } = response
            const { sort } = params
            let row = (current - 1) * pageSize;
            setPermissions(permissions.map<PermissionType>((permission: PermissionType) => ({ ...permission, row: ++row, key: permission.id })))
            setLoadingRecords(false)
            setSort(sort)
            setPagination({
                ...pagination,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({});
        const { current, pageSize } = pagination
        getPermissions({ page: current, limit: pageSize, sort })
    }, [name, caption]);

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getPermissions({ page: current, limit: pageSize, sort });
    };

    return (
        <>
            <Modal destroyOnClose={true} title={Share.addOrEditRecord} cancelText={Share.cancel} okText={Share.save} open={isModalOpen} onOk={() => form.submit()} onCancel={() => setIsModalOpen(false)}>
                <CURecord Form={Form} form={form} record={record} getPermissions={getPermissions} pagination={pagination} sort={sort} />
            </Modal>
            <Table
                columns={columns}
                dataSource={permissions}
                pagination={pagination}
                loading={loadingRecords}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Space.Compact block style={{marginTop:'10px'}}>
                <Button key='add' type="primary" style={{height:'40px'}} icon={<FaPlus />} onClick={() => {
                    setRecord(null)
                    setIsModalOpen(true)
                }}>{ Share.add }</Button>
                </Space.Compact>
            </Affix>
        </>
    )
}
